import { api } from './apiClient';

const MCQ_TYPES = ['text_text', 'audio_text', 'audio_audio', 'image_text'];

const buildOnboardingPayload = (data, isUpdate = false) => {
  const questionType = data.question_type || 'text_text';
  const payload = {};

  if (isUpdate) {
    payload.question_id = data.question_id || data.id;
  } else {
    payload.question_type = questionType;
    payload.order = Number(data.order ?? 0);
  }

  if (data.question_type !== undefined) payload.question_type = data.question_type;
  if (data.order !== undefined) payload.order = Number(data.order);
  if (data.question_text !== undefined) payload.question_text = data.question_text;
  if (data.explanation !== undefined) payload.explanation = data.explanation;
  if (data.points !== undefined && data.points !== '') payload.points = Number(data.points);
  if (data.coins !== undefined && data.coins !== '') payload.coins = Number(data.coins);
  if (typeof data.is_active === 'boolean') payload.is_active = data.is_active;
  if (data.question_audio_url !== undefined) payload.question_audio_url = data.question_audio_url;
  if (data.question_image_url !== undefined) payload.question_image_url = data.question_image_url;

  const includeTypeFields =
    !isUpdate ||
    data.question_type !== undefined ||
    data.options !== undefined ||
    data.match_pairs !== undefined ||
    data.reorder_correct_order !== undefined ||
    data.fill_blank_correct_order !== undefined ||
    data.correct_answer_index !== undefined;

  if (!includeTypeFields) {
    return payload;
  }

  const typeForFields = data.question_type || questionType;

  if (typeForFields === 'match_pairs') {
    const pairs = data.match_pairs || [];
    if (pairs.length > 0) {
      payload.match_pairs = pairs
        .filter((p) => (p.left_item || '').trim() && (p.right_item || '').trim())
        .map((pair, index) => ({
          left_item: pair.left_item.trim(),
          right_item: pair.right_item.trim(),
          order: pair.order !== undefined ? pair.order : index,
        }));
    }
  } else if (typeForFields === 'reorder_text') {
    if (data.reorder_correct_order !== undefined) {
      payload.reorder_correct_order = (data.reorder_correct_order || [])
        .map((item) => (typeof item === 'string' ? item : item.text || ''))
        .filter((text) => text.trim());
    }
  } else if (typeForFields === 'fill_blank_text') {
    if (data.fill_blank_correct_order !== undefined) {
      payload.fill_blank_correct_order = (data.fill_blank_correct_order || []).map((seg) => ({
        title: seg.title ?? '',
        isBlank: Boolean(seg.isBlank),
      }));
    }
  } else if (MCQ_TYPES.includes(typeForFields)) {
    if (Array.isArray(data.options)) {
      const validOptions = data.options.filter((opt) => (opt.text || opt.option_text || '').trim());
      const correctIndex = data.correct_answer_index !== undefined ? data.correct_answer_index : 0;
      const options = validOptions.map((option, index) => ({
        option_text: (option.text || option.option_text || '').trim(),
        is_correct: index === correctIndex,
        order: index + 1,
      }));
      if (options.length > 0) payload.options = options;
    }
  }

  return payload;
};

/**
 * List onboarding questions (paginated, filterable)
 * POST /api/v1/admin/onboarding-questions/list
 */
export const fetchOnboardingQuestionsList = async (
  page = 1,
  limit = 10,
  filters = {}
) => {
  try {
    const body = {
      page,
      limit,
      search: filters.search || '',
      sort_by: filters.sort_by || 'order',
      sort_order: filters.sort_order || 'asc',
    };
    if (filters.is_active !== undefined && filters.is_active !== null && filters.is_active !== 'all') {
      body.is_active = filters.is_active;
    }

    const response = await api.post('/api/v1/admin/onboarding-questions/list', body);
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(
        error.response.data?.message || `Server error: ${error.response.status}`
      );
    }
    if (error.request) {
      throw new Error(
        'Unable to reach the server. Please check your internet connection or try again later.'
      );
    }
    throw new Error(error.message || 'An unexpected error occurred');
  }
};

/**
 * Get a single onboarding question
 * GET /api/v1/admin/onboarding-questions/:question_id
 */
export const getOnboardingQuestion = async (questionId) => {
  if (!questionId) throw new Error('Question ID is required');
  try {
    const response = await api.get(`/api/v1/admin/onboarding-questions/${questionId}`);
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(
        error.response.data?.message || `Server error: ${error.response.status}`
      );
    }
    throw new Error(error.message || 'An unexpected error occurred');
  }
};

/**
 * Create onboarding question
 * POST /api/v1/admin/onboarding-questions
 */
export const createOnboardingQuestion = async (quizData) => {
  try {
    const payload = buildOnboardingPayload(quizData, false);
    const response = await api.post('/api/v1/admin/onboarding-questions', payload);
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(
        error.response.data?.message || `Server error: ${error.response.status}`
      );
    }
    if (error.request) {
      throw new Error(
        'Unable to reach the server. Please check your internet connection or try again later.'
      );
    }
    throw new Error(error.message || 'An unexpected error occurred');
  }
};

/**
 * Update onboarding question
 * PUT /api/v1/admin/onboarding-questions
 */
export const updateOnboardingQuestion = async (quizData) => {
  if (!quizData?.question_id && !quizData?.id) {
    throw new Error('Question ID is required to update a question');
  }
  try {
    const payload = buildOnboardingPayload(
      { ...quizData, question_id: quizData.question_id || quizData.id },
      true
    );
    const response = await api.put('/api/v1/admin/onboarding-questions', payload);
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(
        error.response.data?.message || `Server error: ${error.response.status}`
      );
    }
    if (error.request) {
      throw new Error(
        'Unable to reach the server. Please check your internet connection or try again later.'
      );
    }
    throw new Error(error.message || 'An unexpected error occurred');
  }
};

/**
 * Delete onboarding question
 * DELETE /api/v1/admin/onboarding-questions/:id
 */
export const deleteOnboardingQuestion = async (questionId) => {
  if (!questionId) throw new Error('Question ID is required');
  try {
    const response = await api.delete(`/api/v1/admin/onboarding-questions/${questionId}`);
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(
        error.response.data?.message || `Server error: ${error.response.status}`
      );
    }
    throw new Error(error.message || 'An unexpected error occurred');
  }
};

/**
 * Upload onboarding questions JSON
 * POST /api/v1/admin/onboarding-questions/upload-json
 */
export const uploadOnboardingJson = async (file) => {
  if (!file) throw new Error('JSON file is required');
  try {
    const formData = new FormData();
    formData.append('json_file', file);
    const response = await api.post('/api/v1/admin/onboarding-questions/upload-json', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 6000000,
    });
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data?.message || `Server error: ${error.response.status}`);
    }
    throw new Error(error.message || 'Failed to upload JSON');
  }
};
